import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Slider,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  Fade,
  useTheme
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Visibility as VisualIcon,
  Hearing as AuditoryIcon,
  MenuBook as ReadingIcon,
  TouchApp as KinestheticIcon,
  Timeline as SequentialIcon,
  FlashOn as ActiveIcon,
  Science as SensingIcon,
  RecordVoiceOver as VerbalIcon
} from '@mui/icons-material';
import { AssessmentQuestion, LearningStyleProfile, UserResponse } from '../../types/learning';

interface LearningAssessmentProps {
  questions: AssessmentQuestion[];
  onComplete: (profile: LearningStyleProfile, responses: UserResponse[]) => void;
  onCancel: () => void;
}

const LearningAssessment: React.FC<LearningAssessmentProps> = ({
  questions,
  onComplete,
  onCancel
}) => {
  const theme = useTheme();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [responseTimes, setResponseTimes] = useState<Record<string, number>>({});
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [isComplete, setIsComplete] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestionIndex]);

  const handleResponseChange = (questionId: string, response: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: response
    }));
  };

  const handleNext = () => {
    const responseTime = (Date.now() - questionStartTime) / 1000;
    setResponseTimes(prev => ({
      ...prev,
      [currentQuestion.id]: responseTime
    }));

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    const userResponses: UserResponse[] = Object.entries(responses).map(([questionId, response]) => ({
      id: `response-${questionId}`,
      userId: 'current-user',
      questionId,
      response,
      confidenceLevel: 4, // Default confidence
      responseTimeSeconds: responseTimes[questionId] || 0,
      timestamp: new Date().toISOString()
    }));

    const profile = calculateLearningStyleProfile(userResponses, questions);
    setIsComplete(true);
    onComplete(profile, userResponses);
  };

  const calculateLearningStyleProfile = (
    responses: UserResponse[], 
    questions: AssessmentQuestion[]
  ): LearningStyleProfile => {
    const profile: LearningStyleProfile = {
      visual: 0,
      auditory: 0,
      reading: 0,
      kinesthetic: 0,
      sequential: 0,
      active: 0,
      sensing: 0,
      verbal: 0
    };

    let totalWeight = 0;

    responses.forEach(response => {
      const question = questions.find(q => q.id === response.questionId);
      if (!question) return;

      const responseValue = getResponseValue(response.response, question.questionType);
      const weight = question.learningStyleWeight;

      Object.keys(profile).forEach(key => {
        const styleKey = key as keyof LearningStyleProfile;
        if (weight[styleKey]) {
          profile[styleKey] += responseValue * (weight[styleKey] || 0);
          totalWeight += weight[styleKey] || 0;
        }
      });
    });

    // Normalize the profile
    Object.keys(profile).forEach(key => {
      const styleKey = key as keyof LearningStyleProfile;
      profile[styleKey] = totalWeight > 0 ? profile[styleKey] / totalWeight : 0.5;
    });

    return profile;
  };

  const getResponseValue = (response: any, questionType: string): number => {
    switch (questionType) {
      case 'likert_scale':
        const likertMap = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
        return (likertMap.indexOf(response) + 1) / 5;
      case 'multiple_choice':
        return 1; // Binary scoring for now
      case 'self_reflection':
        const confidenceMap = ['Not confident at all', 'Slightly confident', 'Moderately confident', 'Very confident', 'Extremely confident'];
        return (confidenceMap.indexOf(response) + 1) / 5;
      case 'learning_preference':
        return 1; // All preferences are equally valid
      default:
        return 0.5;
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.questionType) {
      case 'multiple_choice':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2 }}>
              {currentQuestion.questionText}
            </FormLabel>
            <RadioGroup
              value={responses[currentQuestion.id] || ''}
              onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'likert_scale':
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {currentQuestion.questionText}
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={responses[currentQuestion.id] || 3}
                onChange={(_, value) => handleResponseChange(currentQuestion.id, value)}
                min={1}
                max={5}
                step={1}
                marks={[
                  { value: 1, label: 'Strongly disagree' },
                  { value: 2, label: 'Disagree' },
                  { value: 3, label: 'Neutral' },
                  { value: 4, label: 'Agree' },
                  { value: 5, label: 'Strongly agree' }
                ]}
                valueLabelDisplay="off"
                sx={{ mt: 2 }}
              />
            </Box>
          </Box>
        );

      case 'self_reflection':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2 }}>
              {currentQuestion.questionText}
            </FormLabel>
            <RadioGroup
              value={responses[currentQuestion.id] || ''}
              onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'learning_preference':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2 }}>
              {currentQuestion.questionText}
            </FormLabel>
            <RadioGroup
              value={responses[currentQuestion.id] || ''}
              onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'scenario':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2 }}>
              {currentQuestion.questionText}
            </FormLabel>
            <RadioGroup
              value={responses[currentQuestion.id] || ''}
              onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      default:
        return (
          <Typography variant="body1">
            {currentQuestion.questionText}
          </Typography>
        );
    }
  };

  const getLearningStyleIcon = (style: string) => {
    const icons = {
      visual: <VisualIcon />,
      auditory: <AuditoryIcon />,
      reading: <ReadingIcon />,
      kinesthetic: <KinestheticIcon />,
      sequential: <SequentialIcon />,
      active: <ActiveIcon />,
      sensing: <SensingIcon />,
      verbal: <VerbalIcon />
    };
    return icons[style as keyof typeof icons] || <PsychologyIcon />;
  };

  const getLearningStyleColor = (style: string) => {
    const colors = {
      visual: theme.palette.primary.main,
      auditory: theme.palette.secondary.main,
      reading: theme.palette.success.main,
      kinesthetic: theme.palette.warning.main,
      sequential: theme.palette.info.main,
      active: theme.palette.error.main,
      sensing: theme.palette.primary.main,
      verbal: theme.palette.secondary.main
    };
    return colors[style as keyof typeof colors] || theme.palette.grey[500];
  };

  if (isComplete) {
    const profile = calculateLearningStyleProfile(
      Object.entries(responses).map(([questionId, response]) => ({
        id: `response-${questionId}`,
        userId: 'current-user',
        questionId,
        response,
        confidenceLevel: 4,
        responseTimeSeconds: responseTimes[questionId] || 0,
        timestamp: new Date().toISOString()
      })),
      questions
    );

    return (
      <Fade in timeout={500}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 3, color: 'primary.main' }}>
            🎉 Assessment Complete!
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 4 }}>
            Your Learning Style Profile
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
            {Object.entries(profile).map(([style, value]) => (
              <Card key={style} sx={{ textAlign: 'center' }}>
                <CardContent>
                  <Box sx={{ color: getLearningStyleColor(style), mb: 1 }}>
                    {getLearningStyleIcon(style)}
                  </Box>
                  <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                    {style.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={value * 100} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption">
                    {Math.round(value * 100)}%
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Based on your responses, we'll personalize your learning experience to match your preferences!
          </Alert>

          <Button 
            variant="contained" 
            size="large"
            onClick={() => onComplete(profile, [])}
          >
            Start Learning Journey
          </Button>
        </Paper>
      </Fade>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
            Learning Style Assessment
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            Help us understand how you learn best so we can personalize your experience
          </Typography>
          
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            Question {currentQuestionIndex + 1} of {questions.length}
          </Typography>
        </Box>

        {/* Question */}
        <Box sx={{ mb: 4, minHeight: 200 }}>
          {renderQuestion()}
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            variant="outlined"
          >
            Previous
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {questions.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index <= currentQuestionIndex ? 'primary.main' : 'grey.300'
                }}
              />
            ))}
          </Box>

          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!responses[currentQuestion.id]}
          >
            {currentQuestionIndex === questions.length - 1 ? 'Complete' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default LearningAssessment;
